import { Box, Stack } from '@mui/material';
import {
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import React, { ReactElement } from 'react';
import { DataFiltersModel, RangeInput } from '../../models';
import { getEndOfDay, is, transformDate } from '../../utils';
import { validateAndGetPeriod } from '../date-filters';
import { GtDatePicker } from './GtDatePicker';

interface Props {
  disableFuture?: boolean;
  startText?: string;
  endText?: string;
  className?: string;
  disabled?: boolean;
  value: RangeInput;
  onChange: (newValue: RangeInput) => void;
  onFiltersChange?: (filters: DataFiltersModel) => void;
}

export function DateRangePicker({
  disableFuture,
  startText,
  endText,
  disabled,
  value,
  className,
  onChange,
  onFiltersChange,
}: Props): ReactElement {
  const today = getEndOfDay();
  const endDate = getEndOfDay(value[1] ? new Date(value[1]) : undefined);

  const onPeriodDateChange = (values: RangeInput) => {
    if (
      !values ||
      values.length !== 2 ||
      !is.validDate(new Date(values[0])) ||
      !is.validDate(new Date(values[1]))
    ) {
      return;
    }

    onChange(values);

    if (!values[0] || !values[1]) {
      return;
    }

    const validDates = validateAndGetPeriod(values as [Date, Date]);

    onFiltersChange({
      dateFrom: transformDate(validDates[0]),
      dateTo: transformDate(validDates[1]),
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack direction="row" className={className}>
        <Box>
          <GtDatePicker
            label={startText}
            value={value[0] || null}
            disabled={disabled}
            size="small"
            onChange={(newValue: Date | null) => {
              onPeriodDateChange([newValue, value[1]]);
            }}
            renderDay={(
              date: Date,
              selectedDates: Date[],
              pickersDayProps: PickersDayProps<Date>,
            ) => {
              const currentDate = date && new Date(date);
              const disabled =
                !today ||
                !currentDate ||
                currentDate.getTime() > today ||
                (endDate && currentDate.getTime() > endDate);

              return (
                <PickersDay
                  {...pickersDayProps}
                  disabled={disableFuture && disabled}
                />
              );
            }}
          />
        </Box>

        <Box sx={{ padding: '8px 10px', lineHeight: '20px', fontSize: '16px' }}>
          {' '}
          -{' '}
        </Box>

        <Box>
          <GtDatePicker
            label={endText}
            value={value[1] || null}
            disabled={disabled}
            size="small"
            onChange={(newValue: Date | null) => {
              onPeriodDateChange([value[0], newValue]);
            }}
            renderDay={(
              date: Date,
              selectedDates: Date[],
              pickersDayProps: PickersDayProps<Date>,
            ) => {
              const currentDate = date && new Date(date);
              const disabled =
                !today || !currentDate || currentDate.getTime() > today;

              return (
                <PickersDay
                  {...pickersDayProps}
                  disabled={disableFuture && disabled}
                />
              );
            }}
          />
        </Box>
      </Stack>
    </LocalizationProvider>
  );
}
